// ClearCollect demo tour — 20 feature callouts for booth / conference demos.
//
// Each step points at a real UI element (via a `data-tour="..."` selector)
// and gives the booth presenter a short headline + sub-copy to speak to.
//
// The `view` field tells the TourProvider which app state must be active
// before the step renders. The provider handles auto-login + navigation.

export type TourView =
  | 'login'
  | 'parent-dashboard'
  | 'fee-catalog'
  | 'fee-catalog-reduced'   // Same catalog, but switched to Liam (reduced-tier student) for step 6.
  | 'cart'                  // Empty cart (legacy — kept for the step picker).
  | 'cart-seeded'           // Cart with demo items pre-populated for steps 8 + 9.
  | 'checkout'              // Empty checkout (legacy — kept for the step picker).
  | 'checkout-seeded'       // Checkout with the seeded cart so step 11 has real line items.
  | 'purchases'
  | 'events'
  | 'community-shop'
  | 'admin-login'
  | 'admin-dashboard'
  | 'admin-fees'
  | 'admin-preassess'
  | 'admin-orders'
  | 'admin-bulk-import'
  | 'admin-notifications'
  | 'pos-login'
  | 'pos-terminal';

export interface TourHighlight {
  target: string;
  label: string;
  shape?: 'rect' | 'circle';
  /** Where to position the target on screen when the spotlight scrolls
   *  to it. `'center'` (default) keeps the existing behaviour. `'top'`
   *  scrolls so the target lands ~96px below the viewport top, which
   *  is consistent across viewport heights — useful for big monitors
   *  where centring puts the target awkwardly low. */
  scrollAnchor?: 'center' | 'top';
  /** Optional trigger name dispatched as `cc-tour-trigger` when this
   *  highlight becomes active (per-highlight equivalent of `step.trigger`).
   *  Useful when a single step needs to first call out a control, then
   *  trigger the modal/overlay it opens and call that out too. */
  trigger?: string;
  /** Optional follow-up: after `afterMs` into the focus phase, the
   *  spotlight switches from `target` to `followUp.target` and the
   *  optional `followUp.trigger` is dispatched. When `cursor` is true
   *  an animated mouse pointer flies in from off-screen, lands on the
   *  initial target, plays a click ripple, and *then* the trigger
   *  fires (so it reads as "the cursor opened the modal"). All of this
   *  happens inside the highlight's existing autoplay window — the
   *  callout count and step time are unchanged. */
  followUp?: TourFollowUp;
  /** Chain of follow-ups, run in order by their `afterMs` (each
   *  measured from focus phase start, NOT cumulative). Useful for a
   *  multi-action narrative inside a single callout — e.g. spotlight
   *  card → cursor click Add → spotlight cart icon → cursor click →
   *  spotlight item → cursor click delete. Mutually exclusive with
   *  `followUp` — provide one or the other. */
  followUps?: TourFollowUp[];
}

export interface TourFollowUp {
  afterMs: number;
  /** New spotlight target. If omitted, the spotlight stays on the
   *  current target (lets a follow-up fire a trigger / animate a
   *  cursor without moving the spotlight). */
  target?: string;
  /** New scroll anchor for the spotlight target. Defaults to 'center'. */
  scrollAnchor?: 'center' | 'top';
  trigger?: string;
  cursor?: boolean;
  /** Where the cursor lands. Defaults to the current spotlight target.
   *  Use this when the spotlight should cover a wide area (e.g. a
   *  whole card) but the cursor should click a specific control
   *  inside it. */
  cursorTarget?: string;
  /** Pixel offset applied to the cursor's landing point relative
   *  to the centre of the cursor target rect. */
  cursorOffset?: { x: number; y: number };
  /** Force the cursor to re-enter from off-screen (instead of
   *  tweening from its previous landing position). Use after the
   *  page has scrolled or layout has shifted between cursor beats —
   *  the previous cursor position is no longer meaningful and a
   *  fresh entry reads better than a stale tween. */
  cursorRespawn?: boolean;
}

export interface TourStep {
  id: string;
  number: string;         // "01" … "20" — shown as a badge
  title: string;
  tagline: string;        // short headline (italic quote under title)
  description: string;    // the longer "why this matters" copy
  view: TourView;
  highlights: TourHighlight[];
  /** Layout mode for the callout card. Cards always dock to an edge —
   *  they never float centered over the page, which makes the demo feel
   *  steadier at a booth. The chosen side is honoured exactly — there's
   *  no viewport-based auto-pick — so changes to one step never shift
   *  another step's card.
   *  - 'left' / 'right': docks full-height to the named edge.
   *  - 'bottom' / 'top': docks full-width to the named edge.
   *  - 'hero': centred fullscreen closer with its own design (no
   *    spotlight, no rail) — for the final pricing/sales pitch.
   *  - Defaults to 'bottom' if omitted. */
  cardLayout?: 'left' | 'right' | 'bottom' | 'top' | 'hero';
  /** Optional presenter cue shown at the bottom of the card — e.g. for
   *  features that live inside a dialog the tour can't auto-open. */
  presenterCue?: string;
  /** Optional trigger name. When the step activates, TourProvider
   *  dispatches a `cc-tour-trigger` CustomEvent with this name in
   *  `detail.trigger`. Host components listen for the names they know
   *  about and take the side effect (e.g. opening a modal so the demo
   *  can call out a feature that normally lives behind a click). */
  trigger?: string;
  /** Per-step autoplay duration override (ms per highlight). Use when
   *  a step has a long internal followUp chain that needs more time
   *  than the global default. Cleared on next step. */
  autoplayMs?: number;
  /** Per-step overview duration override. Defaults to OVERVIEW_MS (3s).
   *  When the spotlight target requires a long scroll to come into
   *  view, the overview phase still needs to wait for scroll to
   *  complete — the phase machine guarantees `focus` doesn't start
   *  until the rect has stabilized. Use this to cap the *minimum*
   *  overview time after scroll settles. */
  overviewMs?: number;
}

/** CTA shown in the overlay footer. Scannable by booth attendees. */
export const DEMO_URL = 'https://demo.clearcollectforschools.com';
export const DEMO_CTA_LABEL = 'Scan to try it yourself';

// NOTE: Selectors below assume we've added `data-tour="..."` markers to the
// corresponding components. The tour will still advance (card-only) if a
// target isn't found — so we can iterate on markers without breaking the demo.

export const TOUR_STEPS: TourStep[] = [
  {
    id: 'multi-role-login',
    number: '01',
    title: 'Multi-Role Portal',
    tagline: 'One app, three audiences — students, parents, and community members.',
    description:
      'A single URL welcomes every stakeholder. Role tabs adapt the sign-in flow (and the rest of the app) to the right context, so districts only need to market one link.',
    view: 'login',
    cardLayout: 'right',
    highlights: [
      { target: '[data-tour="login-role-tabs"]', label: 'Students, parents, and community members all sign in here — one URL for the whole school community.' },
    ],
  },
  {
    id: 'parent-dashboard',
    number: '02',
    title: 'Family Dashboard',
    tagline: 'Outstanding balance and payment history at a glance.',
    description:
      'Parents land on a single view that answers "what do I owe, for which child, and what did I already pay?" — the #1 reason they call the front office today.',
    view: 'parent-dashboard',
    cardLayout: 'left',
    highlights: [
      { target: '[data-tour="dashboard-balance"]', label: "See exactly what's owed for each child and what's already been paid — answers the #1 question parents call the office about." },
    ],
  },
  {
    id: 'student-switcher',
    number: '03',
    title: 'Multi-Student Switching',
    tagline: 'One login, every child in the family.',
    description:
      'Parents with multiple kids switch between them in one click — no separate accounts, no re-authentication, no copying fees between wallets.',
    view: 'parent-dashboard',
    cardLayout: 'bottom',
    highlights: [
      {
        target: '[data-tour="student-switcher"]',
        label: 'Switch between siblings in one click — one login covers the whole family, no separate accounts to manage.',
        followUp: {
          afterMs: 2800,
          target: '[data-tour="student-selector-dialog"]',
          trigger: 'open-student-selector',
          cursor: true,
          // Pull the cursor 32px right of the pill centre so it lands
          // on the swap-student icon rather than on the user name.
          cursorOffset: { x: 32, y: 0 },
        },
      },
    ],
  },
  {
    id: 'pre-assessed-fees',
    number: '04',
    title: 'Pre-Assessed Fees',
    tagline: 'The right fees, auto-assigned to the right students.',
    description:
      'When enrollment, courses, or grade level determines a fee, ClearCollect assigns it automatically. No manual cart loading, no missed fees, no errors — and updates cascade when an amount changes.',
    view: 'parent-dashboard',
    cardLayout: 'bottom',
    highlights: [
      {
        // Comma-separated multi-target — the spotlight unions the
        // header row with the first two pre-assessed fee cards so the
        // booth viewer sees both the controls and the items they apply
        // to in one frame.
        target: '[data-tour="dashboard-preassessed-header"], [data-tour="dashboard-preassessed-fee"]:nth-of-type(-n+2)',
        label: 'Fees assigned by enrollment, course, or grade — ready to pay in one click.',
        scrollAnchor: 'top',
      },
    ],
  },
  {
    id: 'fee-catalog',
    number: '05',
    title: 'Visual Fee Catalog',
    tagline: 'A familiar e-commerce shopping experience.',
    description:
      'Card-based layout with images, descriptions, and clear pricing turns "pay the school" into a two-minute transaction that parents already know how to complete.',
    view: 'fee-catalog',
    cardLayout: 'right',
    highlights: [
      {
        target: '[data-tour="catalog-search"]',
        label: 'Find any fee in seconds — the catalog filters live as parents type the name, course, or category.',
        followUp: {
          afterMs: 2400,
          target: '[data-tour="catalog-grid"]',
          trigger: 'fill-catalog-search',
          cursor: true,
        },
      },
    ],
  },
  {
    id: 'free-reduced-pricing',
    number: '06',
    title: 'Free & Reduced Pricing',
    tagline: 'Equity pricing applied automatically — no stigma, no paperwork dance.',
    description:
      'Eligible students see their reduced or free price automatically. No tier selection in front of classmates, no after-the-fact refund processing, no gatekeeping at the front office.',
    view: 'admin-fees',
    cardLayout: 'bottom',
    // Step entry: tag the showcase fee's Edit button so the cursor
    // can find it. Dialog stays closed until the cursor "clicks".
    // The Edit Fee dialog reads --tour-bottom-rail-height to lift its
    // centring above the rail so the pricing tier fields stay visible.
    trigger: 'mark-tier-fee-showcase',
    highlights: [
      {
        target: '[data-tour="fee-edit-tour-target"]',
        label: 'Set Base, Reduced, and Free prices once — every eligible student sees their tier automatically, with no front-office gatekeeping.',
        // Hold on the listing for ~2s so the buyer absorbs the admin
        // fee table before the cursor swoops in. Cursor flight is
        // 1100ms, so cursor enters at t≈2000ms and lands at t=3100ms.
        followUp: {
          afterMs: 3100,
          target: '[data-tour="fee-pricing-tiers"]',
          trigger: 'open-tiered-fee-detail',
          cursor: true,
          // Anchor the pricing tiers to the TOP of the DialogContent
          // scroll area (~96px from top) instead of the centre. With
          // a centred anchor the tiers landed near the bottom of the
          // dialog and got clipped by the bottom rail; top anchor
          // keeps them comfortably above the rail.
          scrollAnchor: 'top',
        },
      },
    ],
  },
  {
    id: 'activity-pass',
    number: '07',
    title: 'Student Activity Pass',
    tagline: 'Season passes for athletics and events — bundled and tracked.',
    description:
      'Sell a single activity pass that unlocks the season. ClearCollect tracks who holds a pass so the POS side can validate entry at every game or event.',
    view: 'fee-catalog',
    cardLayout: 'bottom',
    // ~24s for the full Add → Cart → Remove narrative. Adds a 1s
    // pause before search-typing starts and pushes delete late so
    // the buyer registers the cart line item before it's removed.
    autoplayMs: 24000,
    overviewMs: 800,
    // Step entry: type "Activity Pass" into the catalog search so the
    // pass card filters to the top of the catalog. The typing handler
    // uses a 1000ms initial delay for this trigger (vs 400ms for
    // step 5) so the buyer registers the catalog landing first.
    trigger: 'search-catalog-activity-pass',
    highlights: [
      {
        target: '[data-tour="catalog-activity-pass-card"]',
        label: 'One pass unlocks the whole season — parents buy once, ClearCollect tracks who holds a pass at every gate.',
        scrollAnchor: 'top',
        followUps: [
          // Beat 1 (focus+2500): cursor flies to the Add to Cart
          // button and "clicks". Trigger adds the pass programmatically.
          {
            afterMs: 2500,
            cursorTarget: '[data-tour="catalog-activity-pass-add"]',
            trigger: 'cart-add-activity-pass',
            cursor: true,
          },
          // Beat 2a (focus+5000): spotlight switches to the header
          // cart icon. This may trigger a small scroll-to-top via
          // useTargetRect (the cart icon's bounding rect r.top<80
          // triggers the outOfView path).
          {
            afterMs: 5000,
            target: '[data-tour="header-cart-icon"]',
          },
          // Beat 2b (focus+8000): cursor flight starts at 8000-1100=
          // 6900ms — that's 1900ms after the spotlight switch, enough
          // for the scroll-to-top to settle (SCROLL_MS=1800ms). Use
          // cursorRespawn so the cursor re-enters from off-screen
          // instead of tweening from the (now stale) Add button
          // position across the scrolling page.
          {
            afterMs: 8000,
            cursor: true,
            cursorRespawn: true,
          },
          // Beat 2c (focus+9500): navigate after a ~1s beat.
          {
            afterMs: 9500,
            trigger: 'navigate-to-cart',
          },
          // Beat 3 (focus+11500): on the cart page, spotlight the
          // activity pass line item. No cursor.
          {
            afterMs: 11500,
            target: '[data-tour="cart-activity-pass-item"]',
          },
          // Beat 4 (focus+18500): cursor flies from cart icon down to
          // the delete button and "clicks" — 7s on the line item
          // before delete (was 5s, user wanted longer dwell). Trigger
          // removes the item; ~1.5s of post-cleanup focus before the
          // step advances.
          {
            afterMs: 18500,
            cursorTarget: '[data-tour="cart-activity-pass-remove"]',
            trigger: 'cart-remove-activity-pass',
            cursor: true,
          },
        ],
      },
    ],
  },
  {
    id: 'shopping-cart',
    number: '08',
    title: 'Unified Shopping Cart',
    tagline: 'Combine fees, events, and merch into one checkout.',
    description:
      'Parents don\'t pay four separate times for four separate things. Every purchasable item — fees, event tickets, community merch — moves through the same cart with the same receipt.',
    view: 'cart-seeded',
    cardLayout: 'right',
    highlights: [
      { target: '[data-tour="cart-items"]', label: 'Fees, event tickets, and community merch all check out together — one cart, one receipt, one trip through Stripe.' },
    ],
  },
  {
    id: 'partial-payments',
    number: '09',
    title: 'Flexible Partial Payments',
    tagline: '10% · 25% · 50% · 75% · 100% — families pick what works.',
    description:
      'Preset partial-payment buttons let families spread costs over time. Balance tracking is automatic, so both the district and the parent always know what\'s still owed.',
    view: 'fee-catalog',
    cardLayout: 'right',
    autoplayMs: 18000,
    overviewMs: 800,
    // Step entry: type "Semester Bus Pass" into the catalog search.
    // Filters the grid to a single tall card with partial-payment
    // controls visible.
    trigger: 'search-catalog-bus-pass',
    highlights: [
      {
        target: '[data-tour="catalog-bus-pass-card"]',
        label: 'Families spread costs over time with one tap — pick a partial percentage today, the rest carries as a balance the system tracks automatically.',
        scrollAnchor: 'top',
        followUps: [
          // Beat 1 (focus+3500): cursor flies to the Partial Payment
          // toggle and "clicks". Trigger flips the fee into partial
          // mode (the cursor click is visual-only — no real DOM
          // event), revealing the percentage row underneath.
          {
            afterMs: 3500,
            cursorTarget: '[data-tour="catalog-partial-toggle"]',
            trigger: 'bus-pass-partial-mode-on',
            cursor: true,
          },
          // Beat 2 (focus+7500): cursor flies down to the 50% button
          // and "clicks". Trigger sets the percentage to 50%.
          {
            afterMs: 7500,
            cursorTarget: '[data-tour="catalog-partial-50"]',
            trigger: 'bus-pass-partial-50',
            cursor: true,
          },
        ],
      },
    ],
  },
  {
    id: 'markup-passthrough',
    number: '10',
    title: 'Transaction Fee Pass-Through',
    tagline: 'Optionally mark up fees to cover processing costs.',
    description:
      'Districts can absorb the 3.45% + $0.30 processing fee or pass it through to the buyer — per fee, with the math done automatically so the net to the school is exactly the ticket price.',
    view: 'admin-fees',
    highlights: [
      {
        target: '[data-tour="fee-markup-toggle"]',
        label: 'Absorb the processing fee or pass it to the buyer — per fee, with the math done so the school nets exactly the ticket price.',
        // Anchor the toggle to ~96px from the top of the DialogContent
        // scroll area so it lands well above the bottom rail. The Edit
        // Fee dialog already reads --tour-bottom-rail-height as
        // paddingBottom to lift its centring above the rail (same
        // pattern step 6 uses for the pricing-tier highlight).
        scrollAnchor: 'top',
      },
    ],
    cardLayout: 'bottom',
    trigger: 'open-first-fee-detail',
  },
  {
    id: 'stripe-integration',
    number: '11',
    title: 'Stripe-Powered Payments',
    tagline: 'PCI-compliant processing, instant receipts, refunds in a click.',
    description:
      'ClearCollect rides on Stripe\'s rails — industry-standard security, no card data on our servers, and every major payment method including Apple Pay and Google Pay.',
    view: 'checkout-seeded',
    cardLayout: 'right',
    highlights: [
      { target: '[data-tour="checkout-stripe"]', label: 'Industry-standard Stripe checkout — every major card, Apple Pay, and Google Pay built in. No card data ever touches the school district.' },
    ],
  },
  {
    id: 'questionnaires',
    number: '12',
    title: 'Custom Questionnaires',
    tagline: 'Collect forms, waivers, and permissions without paper.',
    description:
      'Attach a questionnaire to any fee — medical forms for sports, permission slips for field trips, dietary forms for events. Admins build the form once; it appears on every fee that needs it.',
    // Land on the admin dashboard with the sidebar visible. Card on
    // the right so the left-hand admin menu stays in frame for the
    // expand-and-click choreography.
    view: 'admin-dashboard',
    cardLayout: 'right',
    autoplayMs: 16000,
    overviewMs: 800,
    highlights: [
      {
        // Highlight target = the questionnaire edit dialog. The
        // dialog only mounts after the final cursor click, so the
        // rect stays null until then — no spotlight, no dim, just the
        // cursor moving naturally through the menu. As soon as the
        // dialog opens, the spotlight materialises on it. (The
        // overview phase advances on minOverviewMs even when no rect
        // resolves; followUps run normally once focus starts.)
        target: '[data-tour="questionnaire-edit-dialog"]',
        label: 'Build a form once, attach it to any fee — medical authorisations, dietary preferences, and permission slips collected at checkout.',
        followUps: [
          // Beat 1 (focus+2500): cursor flies to the Communication &
          // Forms group toggle and "clicks". Trigger expands the group
          // (handler in AdminDashboard listens for
          // 'expand-communication-menu').
          {
            afterMs: 2500,
            cursorTarget: '[data-tour="admin-menu-group-communication"]',
            trigger: 'expand-communication-menu',
            cursor: true,
          },
          // Beat 2 (focus+5500): cursor flies to Questionnaires and
          // "clicks". 3000ms after the expand trigger gives the
          // Collapse animation time to settle before the cursor moves.
          // Trigger navigates to the questionnaires page.
          {
            afterMs: 5500,
            cursorTarget: '[data-tour="admin-menu-item-questionnaires"]',
            trigger: 'navigate-to-questionnaires',
            cursor: true,
          },
          // Beat 3 (focus+9000): cursor flies to the Spirit Wear
          // card's Edit button and "clicks". cursorRespawn so the
          // cursor re-enters from off-screen instead of tweening
          // across the page change. Trigger opens the edit dialog —
          // the spotlight materialises on it the moment it mounts
          // (rect resolves, dim fades in over TRANSITION_MS).
          {
            afterMs: 9000,
            cursorTarget: '[data-tour="questionnaire-edit-spirit-wear"]',
            trigger: 'open-spirit-wear-edit',
            cursor: true,
            cursorRespawn: true,
          },
        ],
      },
    ],
  },
  {
    id: 'digital-signatures',
    number: '13',
    title: 'Digital Signatures',
    tagline: 'Permission slips signed on the device, attached to the order.',
    description:
      'Field-trip waivers and other permission forms appear inline at Add to Cart — accept terms, type a name, sign on the canvas, and the signature is stored alongside the order. No printing, no scanning, no chasing parents for paper.',
    view: 'fee-catalog',
    cardLayout: 'right',
    autoplayMs: 20000,
    overviewMs: 800,
    // Step entry: type "Science Museum Trip" into the catalog search.
    // Filters the grid down to the museum trip card so the cursor's
    // Add to Cart click has a stable target.
    trigger: 'search-catalog-museum-trip',
    highlights: [
      {
        target: '[data-tour="catalog-museum-trip-card"]',
        label: 'Field-trip waivers signed right at checkout — the saved signature attaches to the order so the office never chases paper slips.',
        scrollAnchor: 'top',
        followUps: [
          // Beat 1 (focus+3500): cursor flies to Add to Cart, "clicks",
          // trigger opens the signature dialog, and the spotlight
          // switches to the dialog on the SAME tick — no perceptible
          // gap. fireTrigger dispatches the trigger and advances
          // followUpIndex in one call, so effectiveTarget recomputes
          // to sig-dialog the moment the dialog mounts.
          {
            afterMs: 3500,
            cursorTarget: '[data-tour="catalog-museum-trip-add"]',
            trigger: 'cart-add-museum-trip',
            target: '[data-tour="sig-dialog"]',
            cursor: true,
          },
          // Beat 2 (focus+6000): cursor "clicks" the I-agree checkbox.
          // Trigger sets `accepted=true` (handler in SignatureDialog).
          // cursorRespawn so the cursor re-enters from off-screen
          // instead of tweening from the (now hidden) Add button.
          {
            afterMs: 6000,
            cursorTarget: '[data-tour="sig-accept-checkbox"]',
            trigger: 'sig-accept-terms',
            cursor: true,
            cursorRespawn: true,
          },
          // Beat 3 (focus+8500): cursor "clicks" the Full Name field.
          // Trigger types "Lisa Johnson" letter-by-letter.
          {
            afterMs: 8500,
            cursorTarget: '[data-tour="sig-name-field"]',
            trigger: 'sig-fill-name',
            cursor: true,
          },
          // Beat 4 (focus+12500): cursor moves to the signature canvas.
          // Trigger animates a cursive scribble onto the canvas (~1.5s
          // of progressive drawing) and flips hasDrawnSignature on.
          {
            afterMs: 12500,
            cursorTarget: '[data-tour="sig-canvas"]',
            trigger: 'sig-draw-signature',
            cursor: true,
          },
          // Beat 5 (focus+16500): final spotlight on the dialog
          // (re-pin in case the canvas grew the rect), showing all
          // three actions complete. ~3s of dwell before step advances.
          {
            afterMs: 16500,
            target: '[data-tour="sig-dialog"]',
          },
        ],
      },
    ],
  },
  {
    id: 'digital-tickets',
    number: '14',
    title: 'Digital Tickets + Wallet',
    tagline: 'Apple Wallet & Google Pay tickets with QR codes.',
    description:
      'After payment, parents get digital tickets with QR codes ready to drop into Apple Wallet or Google Pay. No paper, no re-printing, no "I left it at home."',
    view: 'purchases',
    cardLayout: 'right',
    autoplayMs: 14000,
    overviewMs: 800,
    highlights: [
      {
        target: '[data-tour="purchase-ticket-card"]',
        label: 'Wallet-ready tickets with QR codes — drop them into Apple Wallet or Google Pay and scan straight at the gate. No paper, no reprinting.',
        scrollAnchor: 'top',
        followUps: [
          // Single beat: cursor flies to View Tickets, "clicks", and
          // the spotlight switches straight to the dialog at the same
          // moment the trigger fires (i.e. when the dialog opens).
          // Combining cursor + trigger + target on one followUp means
          // there's no perceptible gap between click and spotlight
          // landing on the modal — fireTrigger dispatches the trigger
          // AND advances followUpIndex in the same tick, so
          // effectiveTarget recomputes to wallet-pass-dialog right as
          // the dialog mounts.
          {
            afterMs: 3000,
            cursorTarget: '[data-tour="purchase-view-tickets-btn"]',
            trigger: 'open-first-ticket-wallet',
            target: '[data-tour="wallet-pass-dialog"]',
            cursor: true,
          },
        ],
      },
    ],
  },
  {
    id: 'pos-concessions',
    number: '15',
    title: 'POS Portal · Ticketing + Concessions',
    tagline: 'Event gate + concession stand, running off the same catalog.',
    description:
      'Worker-facing POS terminal handles ticket validation at the gate and cash/card concession sales inside. Everything syncs back to the same order history and same financial reports.',
    view: 'pos-terminal',
    // Card on the right rail; the POS items grid stays full-width on
    // the left while the cursor rings up an order.
    cardLayout: 'right',
    autoplayMs: 16000,
    overviewMs: 800,
    highlights: [
      {
        // Intentional non-existent selector — keeps the rect null so
        // there's no dim and no spotlight at any point in this step.
        // Phase machine still advances (overviewMs alone, since no
        // rect ever resolves) so the followUp chain runs normally.
        // Booth narrative: a busy concession-stand transaction is
        // self-explanatory; a spotlight would be visual noise.
        target: '[data-tour-noop="pos-concessions"]',
        label: 'Same catalog, same reports — workers scan tickets at the gate and ring up concession sales inside, all on one terminal.',
        followUps: [
          // Beat 1 (focus+2000): cursor "clicks" Pizza Slice — adds 1.
          {
            afterMs: 2000,
            cursorTarget: '[data-tour="pos-item-pizza-slice"]',
            trigger: 'pos-add-pizza-slice',
            cursor: true,
          },
          // Beat 2 (focus+3500): cursor "clicks" Pizza Slice again — adds 2nd.
          {
            afterMs: 3500,
            cursorTarget: '[data-tour="pos-item-pizza-slice"]',
            trigger: 'pos-add-pizza-slice',
            cursor: true,
          },
          // Beat 3 (focus+5000): cursor "clicks" Bottled Water — adds 1.
          {
            afterMs: 5000,
            cursorTarget: '[data-tour="pos-item-bottled-water"]',
            trigger: 'pos-add-bottled-water',
            cursor: true,
          },
          // Beat 4 (focus+7000): cursor "clicks" Charge — opens
          // payment dialog. cursorRespawn so the cursor jumps from
          // the items grid to the cart panel cleanly.
          {
            afterMs: 7000,
            cursorTarget: '[data-tour="pos-charge-btn"]',
            trigger: 'pos-open-payment',
            cursor: true,
            cursorRespawn: true,
          },
          // Beat 5 (focus+9500): cursor "clicks" Process Payment —
          // 2.5s after the dialog opens gives it time to mount and
          // the card reader to auto-select. Trigger fires
          // handleProcessPayment which runs a 2s "processing"
          // animation then flips the dialog to the receipt screen.
          {
            afterMs: 9500,
            cursorTarget: '[data-tour="pos-process-payment-btn"]',
            trigger: 'pos-process-payment',
            cursor: true,
            cursorRespawn: true,
          },
        ],
      },
    ],
  },
  {
    id: 'admin-metrics',
    number: '16',
    title: 'Real-Time Admin Metrics',
    tagline: 'Revenue, collection rates, and outstanding balances — live.',
    description:
      'Finance leaders land on a dashboard that answers "how much have we collected this week, and what\'s still outstanding?" with no spreadsheet gymnastics.',
    view: 'admin-dashboard',
    cardLayout: 'bottom',
    highlights: [
      { target: '[data-tour="admin-metrics"]', label: 'Revenue, orders, and outstanding balances at a glance — finance leaders skip the spreadsheet gymnastics.' },
    ],
  },
  {
    id: 'admin-orders',
    number: '17',
    title: 'Orders & Refunds',
    tagline: 'Every transaction, every line item, refundable in a click.',
    description:
      'A filterable ledger of every payment ever made. Full and partial refunds process through Stripe without leaving the admin. Complete audit trail included.',
    view: 'admin-orders',
    cardLayout: 'bottom',
    highlights: [
      { target: '[data-tour="admin-orders-table"]', label: 'Every payment, searchable and refundable — full and partial refunds process through Stripe without leaving the admin.' },
    ],
  },
  {
    id: 'bulk-import',
    number: '18',
    title: 'Bulk Import Wizard',
    tagline: 'CSV in, mapped fields, preview, confirm — roster ready in minutes.',
    description:
      'The guided importer handles students, parents, fees, and pre-assessed fee assignments. Map your columns, preview the changes, validate, and commit — with rollback if something\'s wrong.',
    view: 'admin-bulk-import',
    cardLayout: 'right',
    autoplayMs: 14000,
    overviewMs: 800,
    // Step entry: drive the bulk-import wizard automatically — picks
    // Courses, pretends to upload a 5-row CSV, advances through field
    // mapping, and lands on the preview screen. STOPS before the
    // final import (no actual rows committed).
    trigger: 'bulk-import-courses-walkthrough',
    highlights: [
      {
        // Non-existent selector → no rect → no spotlight, no dim.
        // Phase machine still advances on overviewMs alone (!hasRect
        // fallback) so the step ticks normally.
        target: '[data-tour-noop="bulk-import"]',
        label: "Drop a CSV, map your columns, preview, commit — full rosters and pre-assessed fee assignments imported in minutes, with rollback if something's off.",
      },
    ],
  },
  {
    id: 'notifications',
    number: '19',
    title: 'Automated Communications',
    tagline: 'Payment confirmations, receipts, and reminders — on autopilot.',
    description:
      'Customizable email templates trigger on payment, on balance, on schedule. Target a course, a grade, or an individual. Full delivery history so "I didn\'t know" goes away.',
    view: 'admin-notifications',
    cardLayout: 'bottom',
    autoplayMs: 12000,
    overviewMs: 800,
    // Step entry: trigger a slow top-to-bottom scroll of the
    // notifications page (handler in NotificationsManagement). No
    // cursor, no spotlight — just a calm pan that lets the booth
    // audience see the breadth of templates in the library.
    trigger: 'notifications-slow-scroll',
    highlights: [
      {
        // Non-existent selector → no rect resolves → no dim, no
        // spotlight. Phase machine still advances on overviewMs
        // alone via the !hasRect fallback.
        target: '[data-tour-noop="notifications"]',
        label: 'Confirmations, receipts, and balance reminders fire automatically — target a course, a grade, or one parent. Full delivery history so "I didn\'t get the message" goes away.',
      },
    ],
  },
  {
    id: 'transparent-pricing',
    number: '20',
    title: 'Predictable Pricing. Real Savings.',
    tagline: 'Conference special — 2.9% + $0.30 for your first year. 3.39% + $0.30 after.',
    description:
      'Built for K-12 districts processing $300,000+ in fees, activities, and tickets annually. One platform, no tip prompts, no per-family caps, no revenue sharing.',
    view: 'login',
    // Hero layout: centred fullscreen closer with its own design.
    // The closing slide is the sales pitch — break it out of the
    // standard rail card and give it room to breathe.
    cardLayout: 'hero',
    highlights: [
      {
        // Non-existent selector — no rect, no spotlight, no dim.
        // The hero card has its own centred backdrop.
        target: '[data-tour-noop="closer"]',
        label: '3.39% + $0.30 per transaction — every feature included, no per-family caps, no hidden charges, no revenue sharing.',
      },
    ],
  },
];

export const TOTAL_CALLOUTS = TOUR_STEPS.reduce((n, s) => n + s.highlights.length, 0);
